import React from "react";
import { Box, Typography, Avatar, Grid, ListItem, List } from "@mui/material";
import { useNavigate } from "react-router-dom";
import moment from 'moment';
import RectangleIcon from "@mui/icons-material/Rectangle";

interface SolutionOwnerInfo {
    id: string;
    name: string;
    email?: string;
    googleProfilePicture?: string | null;
}

// Updated interface for the table
interface DataPipeline {
    id: string;
    projectId: string;
    pipelineName: string;
    region: string;
    status: 'pending' | 'active' | 'rejected' | string;
    environment?: string;
    applicationName: string;
    solutionCategoryType: string;
    cloudProvider: string; // New field
    updatedAt?: string;
    createdAt?: string;
    solutionOwner?: SolutionOwnerInfo | null;
    reasonForRejection?: string;
}

interface DataPipelinesTableProps {
    pipelines: DataPipeline[];
    columnFilters: Record<string, boolean>;
    statusType?: 'pending' | 'active' | 'rejected';
}

const DataPipelinesTable: React.FC<DataPipelinesTableProps> = ({
    pipelines,
    columnFilters,
    statusType,
}) => {
    const [cardHoverIndex, setCardHoverIndex] = React.useState<number | null>(null);
    const navigate = useNavigate();

    
    const handleRowClick = (pipelineId: string) => {
        navigate(`/datasolutions/data-pipelines/details/${pipelineId}`);
    };

    const dateFormatter = (dateValue: string | number | null | undefined) => {
            if (!dateValue) return null;
            const numericDate = Number(dateValue);
            if (isNaN(numericDate)) return null;
            return moment(new Date(numericDate).toISOString()).format("DD/MM/YYYY | hh:mm A");
        };

    // Updated headers for Data Pipelines table
    const headers = [
        { key: "pipelineName", label: "PIPELINE NAME", xs: 2 },
        { key: "projectId", label: "PROJECT ID", xs: 1.5 },
        { key: "applicationName", label: "APPLICATION NAME", xs: 1.5 },
        { key: "solutionCategoryType", label: "CATEGORY TYPE", xs: 1.5 },
        { key: "cloudProvider", label: "CLOUD IDENTIFIER", xs: 1.5 },
        { key: "solutionOwner", label: "RESOURCE OWNER", xs: 2 },
        { key: "createdAt", label: "ADDED ON", xs: 2 },
    ];

    const iconColor = statusType === "active" ? "#12B76A" : statusType === "rejected" ? "#F04438" : "#FDB022";
    const listItemBgColor = statusType === "active" ? "#FFFFFF" : statusType === "rejected" ? "#F0443810" : "#FDB02210";


    return (
        <List sx={{ bgcolor: "transparent", px: 0, mx: 0, width: '100%' }}>
            {/* Table Header */}
            <ListItem sx={{ bgcolor: "#f2f2f2", borderRadius: "4px", mb: 0.5, px: 2, py: 1, width: '100%' }}>
                <Box width="100%">
                    <Grid container direction="row" alignItems="center">
                        {headers.map(header => (
                            columnFilters[header.key] && (
                                <Grid item xs={header.xs as any} key={header.key}>
                                    <Typography fontSize="12px" color="#000000" fontFamily="Gilroy-Medium">
                                        {header.label}
                                    </Typography>
                                </Grid>
                            )
                        ))}
                    </Grid>
                </Box>
            </ListItem>

            {/* Table Body - Pipeline Rows */}
            {pipelines.map((pipeline, index) => (
                <ListItem
                    key={pipeline.id}
                    sx={{
                        mt: 1,
                        borderRadius: "8px",
                        cursor: "pointer",
                        px: 2,
                        width: '100%',
                        backgroundColor: listItemBgColor,
                        '&:hover': {
                            borderColor: "black",
                        }
                    }}
                    onMouseEnter={() => setCardHoverIndex(index)}
                    onMouseLeave={() => setCardHoverIndex(null)}
                    onClick={() => handleRowClick(pipeline.id)}
                >
                    <Box width="100%">
                        <Grid container direction="row" alignItems="center">
                            {columnFilters.pipelineName && (
                                <Grid item xs={headers.find(h => h.key === 'pipelineName')?.xs as any}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <RectangleIcon
                                            sx={{
                                                color: iconColor,
                                                bgcolor: iconColor,
                                                width: "4px",
                                                height: "22px",
                                                marginRight: "8px",
                                            }}
                                        />
                                        <Typography fontSize="14px" fontFamily="Gilroy-Medium" color="text.primary" noWrap>
                                            {pipeline.pipelineName || "N/A"}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            {columnFilters.projectId && (
                                <Grid item xs={headers.find(h => h.key === 'projectId')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                        {pipeline.projectId || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.applicationName && (
                                <Grid item xs={headers.find(h => h.key === 'applicationName')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                        {pipeline.applicationName || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.solutionCategoryType && (
                                <Grid item xs={headers.find(h => h.key === 'solutionCategoryType')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333">
                                        {pipeline.solutionCategoryType || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.cloudProvider && (
                                <Grid item xs={headers.find(h => h.key === 'cloudProvider')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" sx={{textTransform: 'uppercase'}}>
                                        {pipeline.cloudProvider || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.solutionOwner && (
                                <Grid item xs={headers.find(h => h.key === 'solutionOwner')?.xs as any}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <Avatar
                                            src={pipeline.solutionOwner?.googleProfilePicture || undefined}
                                            alt={pipeline.solutionOwner?.name || ""}
                                            sx={{ marginRight: "8px", height: 24, width: 24, fontSize: '0.75rem', bgcolor: pipeline.solutionOwner?.googleProfilePicture ? 'transparent' : 'rgba(0,0,0,0.1)' }}
                                        >
                                            {pipeline.solutionOwner && !pipeline.solutionOwner.googleProfilePicture ? pipeline.solutionOwner.name.charAt(0).toUpperCase() : null}
                                        </Avatar>
                                        <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                            {pipeline.solutionOwner?.name || "N/A"}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            {columnFilters.createdAt && (
                                <Grid item xs={headers.find(h => h.key === 'createdAt')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333">
                                        {dateFormatter(Number(pipeline.createdAt))}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </ListItem>
            ))}
        </List>
    );
};

export default DataPipelinesTable;